import React from 'react'

export interface BoxProps {
  children?: React.ReactNode
  padding?: 'n' | 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl' | React.CSSProperties
  margin?: 'n' | 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl' | React.CSSProperties
  marginTop?: string
  marginBottom?: string
  marginLeft?: string
  marginRight?: string
  float?: 'left' | 'right' | 'none'
  color?: 'text-label' | 'text-body-secondary' | 'text-status-error' | 'inherit' | string
  fontSize?: 'body-s' | 'body-m' | 'body-xs' | 'heading-xs' | 'heading-s' | 'heading-m' | 'heading-l' | 'heading-xl'
  fontWeight?: 'normal' | 'bold' | 'heavy'
  textAlign?: 'left' | 'center' | 'right'
  display?: 'block' | 'inline' | 'inline-block' | 'flex' | 'none'
  variant?: 'div' | 'span' | 'p' | 'strong' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  className?: string
  style?: React.CSSProperties
  // CSS pass-through props used by callers
  backgroundColor?: string
  borderRadius?: string
  border?: string
  gap?: string
  justifyContent?: string
  alignItems?: string
  lineHeight?: string
  onClick?: React.MouseEventHandler<HTMLElement>
}

const spacingMap = {
  n: '0',
  xs: '4px',
  s: '8px',
  m: '16px',
  l: '24px',
  xl: '32px',
  xxl: '40px',
}

const fontSizeMap = {
  'body-xs': '11px',
  'body-s': '12px',
  'body-m': '14px',
  'heading-xs': '16px',
  'heading-s': '18px',
  'heading-m': '20px',
  'heading-l': '24px',
  'heading-xl': '32px',
}

const colorMap = {
  'text-label': '#5f6b7a',
  'text-body-secondary': '#5f6b7a',
  inherit: 'inherit',
}

export const Box: React.FC<BoxProps> = ({
  children,
  padding,
  margin,
  marginTop,
  marginBottom,
  marginLeft,
  marginRight,
  float: floatProp,
  color,
  fontSize,
  fontWeight,
  textAlign,
  display,
  variant = 'div',
  className,
  style,
  backgroundColor,
  borderRadius,
  border,
  gap,
  justifyContent,
  alignItems,
  lineHeight,
  onClick,
}) => {
  const Component = variant

  const resolvedPadding = typeof padding === 'string' ? spacingMap[padding] : undefined
  const resolvedMargin = typeof margin === 'string' ? spacingMap[margin] : undefined

  const boxStyle: React.CSSProperties = {
    padding: resolvedPadding,
    margin: resolvedMargin,
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
    float: floatProp,
    color: color ? (colorMap[color as keyof typeof colorMap] ?? color) : undefined,
    fontSize: fontSize ? fontSizeMap[fontSize] : undefined,
    fontWeight: fontWeight,
    textAlign: textAlign,
    display: display,
    backgroundColor,
    borderRadius,
    border,
    gap,
    justifyContent,
    alignItems,
    lineHeight,
    ...style,
  }

  return (
    <Component className={className} style={boxStyle} onClick={onClick}>
      {children}
    </Component>
  )
}
